import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Search, Plus, X, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';
import { Helmet } from 'react-helmet';
import foodTruckImage from '@/assets/food-truck.jpg';
import { usePublicSubscriptionStatus } from '@/hooks/usePublicSubscriptionStatus';
import { trackConversion } from '@/lib/gtag';

const cateringSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  phone: z.string().trim().max(20, "Phone number must be less than 20 characters").optional(),
  message: z.string().trim().max(1000, "Message must be less than 1000 characters").optional(),
  desiredDate: z.string().min(1, "Desired date is required"),
  numberOfServings: z.number().min(1, "Number of servings must be at least 1"),
  selectedItems: z.array(z.object({
    itemId: z.string(),
    itemName: z.string(),
    price: z.string(),
    quantity: z.number().min(1)
  })).min(1, "Please select at least one item")
});

const Catering = () => {
  const { t } = useLanguage();
  const { hasCateringAccess } = usePublicSubscriptionStatus();
  const [cateringForm, setCateringForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    desiredDate: '',
    numberOfServings: 1,
    selectedItems: [] as Array<{ itemId: string; itemName: string; price: string; quantity: number }>
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [itemQuantities, setItemQuantities] = useState<Record<string, number>>({});
  const [menuSearchQuery, setMenuSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [cateringSettings, setCateringSettings] = useState({
    delivery_percentage: 0,
    sales_tax_percentage: 0,
    gratuity_percentage: 0,
    discount_percentage: 0
  });

  useEffect(() => {
    fetchMenuItems();
    fetchCateringSettings();
  }, []);

  const fetchCateringSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('catering_settings')
        .select('*')
        .single();

      if (error) throw error;
      
      if (data) {
        setCateringSettings({
          delivery_percentage: Number(data.delivery_percentage) || 0,
          sales_tax_percentage: Number(data.sales_tax_percentage) || 0,
          gratuity_percentage: Number(data.gratuity_percentage) || 0,
          discount_percentage: Number(data.discount_percentage) || 0
        });
      }
    } catch (error) {
      console.error('Error fetching catering settings:', error);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const { data, error } = await supabase
        .from('menu_items')
        .select('*')
        .order('name');

      if (error) throw error;
      setMenuItems(data || []);
    } catch (error) {
      console.error('Error fetching menu items:', error);
    }
  };

  const handleCateringSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const validatedData = cateringSchema.parse(cateringForm);

      const { error } = await supabase
        .from('catering_requests')
        .insert([{
          name: validatedData.name,
          email: validatedData.email,
          phone_number: validatedData.phone || null,
          message: validatedData.message || null,
          desired_date: validatedData.desiredDate,
          number_of_servings: validatedData.numberOfServings,
          selected_items: validatedData.selectedItems,
          total_amount: calculateGrandTotal(),
          delivery_percentage: cateringSettings.delivery_percentage,
          sales_tax_percentage: cateringSettings.sales_tax_percentage,
          gratuity_percentage: cateringSettings.gratuity_percentage,
          discount_percentage: cateringSettings.discount_percentage
        }]);

      if (error) throw error;

      // Track enhanced conversion
      trackConversion('catering_request_submitted', {
        email: validatedData.email,
        phone_number: validatedData.phone || undefined,
        address: {
          first_name: validatedData.name.split(' ')[0],
          last_name: validatedData.name.split(' ').slice(1).join(' ') || undefined,
        }
      });

      // Send confirmation email to customer
      try {
        await supabase.functions.invoke("send-catering-request", {
          body: {
            email: validatedData.email,
            name: validatedData.name,
            phone: validatedData.phone || undefined,
            desiredDate: validatedData.desiredDate,
            numberOfServings: validatedData.numberOfServings,
            message: validatedData.message || undefined,
            selectedItems: validatedData.selectedItems.map(item => ({
              name: item.itemName,
              quantity: item.quantity,
              price: calculateItemTotal(item.price, item.quantity),
            })),
            totalAmount: calculateGrandTotal(),
            discountPercentage: cateringSettings.discount_percentage,
            salesTaxPercentage: cateringSettings.sales_tax_percentage,
            gratuityPercentage: cateringSettings.gratuity_percentage,
            deliveryPercentage: cateringSettings.delivery_percentage,
          },
        });
      } catch (emailError) {
        console.error("Error sending catering request email:", emailError);
        // Don't fail the request if email fails
      }

      toast.success(t('cateringSent'));
      
      setCateringForm({
        name: '',
        email: '',
        phone: '',
        message: '',
        desiredDate: '',
        numberOfServings: 1,
        selectedItems: []
      });
      setItemQuantities({});
      setMenuSearchQuery('');
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      } else {
        toast.error(t('cateringFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCateringChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.id === 'numberOfServings' ? parseInt(e.target.value) || 1 : e.target.value;
    setCateringForm({
      ...cateringForm,
      [e.target.id]: value,
    });
  };

  const addMenuItem = (item: any) => {
    const existingIndex = cateringForm.selectedItems.findIndex(i => i.itemId === item.id);
    
    if (existingIndex >= 0) {
      toast.error('Item already added');
      return;
    }
    
    setCateringForm({
      ...cateringForm,
      selectedItems: [...cateringForm.selectedItems, {
        itemId: item.id,
        itemName: item.name,
        price: item.price,
        quantity: 1
      }]
    });
    setItemQuantities({ ...itemQuantities, [item.id]: 1 });
    toast.success(`${item.name} added`);
  };

  const parsePrice = (priceStr: string): number => {
    const numericPrice = priceStr.replace(/[^0-9.]/g, '');
    return parseFloat(numericPrice) || 0;
  };

  const calculateItemTotal = (price: string, quantity: number): number => {
    return parsePrice(price) * quantity;
  };

  const calculateSubtotal = (): number => {
    return cateringForm.selectedItems.reduce((total, item) => {
      return total + calculateItemTotal(item.price, item.quantity);
    }, 0);
  };

  const calculateDeliveryFee = (subtotal: number): number => {
    return (subtotal * cateringSettings.delivery_percentage) / 100;
  };

  const calculateDiscount = (subtotal: number): number => {
    return (subtotal * cateringSettings.discount_percentage) / 100;
  };

  const calculateTax = (subtotalAfterDiscount: number): number => {
    return (subtotalAfterDiscount * cateringSettings.sales_tax_percentage) / 100;
  };

  const calculateGratuity = (subtotalAfterDiscount: number): number => {
    return (subtotalAfterDiscount * cateringSettings.gratuity_percentage) / 100;
  };

  const calculateGrandTotal = (): number => {
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount(subtotal);
    const subtotalAfterDiscount = subtotal - discount;
    const delivery = calculateDeliveryFee(subtotal);
    const tax = calculateTax(subtotalAfterDiscount);
    const gratuity = calculateGratuity(subtotalAfterDiscount);
    return subtotalAfterDiscount + delivery + tax + gratuity;
  };

  const removeMenuItem = (itemId: string) => {
    const newItems = cateringForm.selectedItems.filter(i => i.itemId !== itemId);
    setCateringForm({ ...cateringForm, selectedItems: newItems });
    
    const newQuantities = { ...itemQuantities };
    delete newQuantities[itemId];
    setItemQuantities(newQuantities);
  };

  const updateItemQuantity = (itemId: string, quantity: number) => {
    if (quantity < 1) return;
    
    setItemQuantities({ ...itemQuantities, [itemId]: quantity });
    
    const newItems = cateringForm.selectedItems.map(item =>
      item.itemId === itemId ? { ...item, quantity } : item
    );
    setCateringForm({ ...cateringForm, selectedItems: newItems });
  };

  const filteredMenuItems = menuItems.filter(item =>
    item.name.toLowerCase().includes(menuSearchQuery.toLowerCase()) &&
    !cateringForm.selectedItems.some(selected => selected.itemId === item.id)
  );

  if (!hasCateringAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Catering Unavailable</h1>
          <p className="text-muted-foreground">Catering services are currently disabled.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Catering Services | Junnior's Tacos</title>
        <meta name="description" content="Professional catering services for your events. Choose from our authentic Mexican menu and get a custom quote for your party or gathering." />
      </Helmet>
      
      {/* Hero Section with Background Image */}
      <section 
        className="relative min-h-[40vh] flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `url(${foodTruckImage})` }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-5xl font-bold font-['Playfair_Display'] text-white mb-4">
            Catering Services
          </h1>
          <p className="text-base md:text-lg text-white/90">
            Authentic Mexican cuisine for your special events
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-12 md:py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-2xl md:text-4xl font-bold font-['Playfair_Display'] text-center mb-8 md:mb-12">
            How Our Catering Works
          </h2>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <Card className="p-6 text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-bold mb-2">1. Browse Our Menu</h3>
              <p className="text-sm text-muted-foreground">
                Search and select items from our full menu. Add as many dishes as you need for your event.
              </p>
            </Card>
            
            <Card className="p-6 text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Plus className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-bold mb-2">2. Customize Your Order</h3>
              <p className="text-sm text-muted-foreground">
                Adjust quantities, specify servings, and add special notes about dietary restrictions or preferences.
              </p>
            </Card>
            
            <Card className="p-6 text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-bold mb-2">3. Get Instant Quote</h3>
              <p className="text-sm text-muted-foreground">
                See your total price instantly. Submit your request and we'll confirm availability and details.
              </p>
            </Card>
          </div>
        </div>
      </section>

      <main className="py-12 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-bold font-['Playfair_Display'] mb-4">
              Request Your Catering Quote
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
              Fill out the form below to get started. Select your items, and we'll show you the total cost immediately.
            </p>
          </div>

          <Card className="p-4 md:p-8 max-w-2xl mx-auto">
            <h2 className="text-lg md:text-2xl font-bold mb-4 md:mb-6 text-center">{t('requestCateringInfo')}</h2>
            <form onSubmit={handleCateringSubmit} className="space-y-4 md:space-y-6">
              <div>
                <Label htmlFor="name" className="text-xs md:text-sm">{t('name')} *</Label>
                <Input
                  id="name"
                  value={cateringForm.name}
                  onChange={handleCateringChange}
                  required
                  className="mt-2 text-sm md:text-base h-9 md:h-10"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-xs md:text-sm">{t('email')} *</Label>
                <Input
                  id="email"
                  type="email"
                  value={cateringForm.email}
                  onChange={handleCateringChange}
                  required
                  className="mt-2 text-sm md:text-base h-9 md:h-10"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-xs md:text-sm">{t('phone')}</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={cateringForm.phone}
                  onChange={handleCateringChange}
                  className="mt-2 text-sm md:text-base h-9 md:h-10"
                />
              </div>

              <div>
                <Label htmlFor="desiredDate" className="text-xs md:text-sm">Desired Date *</Label>
                <Input
                  id="desiredDate"
                  type="date"
                  value={cateringForm.desiredDate}
                  onChange={handleCateringChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="mt-2 text-sm md:text-base h-9 md:h-10"
                />
              </div>

              <div>
                <Label htmlFor="numberOfServings" className="text-xs md:text-sm">Number of Servings *</Label>
                <Input
                  id="numberOfServings"
                  type="number"
                  min="1"
                  value={cateringForm.numberOfServings}
                  onChange={handleCateringChange}
                  required
                  className="mt-2 text-sm md:text-base h-9 md:h-10"
                />
              </div>

              <div>
                <Label className="text-xs md:text-sm">Select Menu Items *</Label>
                
                {/* Search Bar */}
                <div className="mt-2 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search menu items..."
                    value={menuSearchQuery}
                    onChange={(e) => setMenuSearchQuery(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                    className="pl-9 text-sm md:text-base h-9 md:h-10"
                  />
                </div>

                {/* Available Items */}
                {isSearchFocused && (
                  <div className="mt-2 border rounded-md max-h-48 overflow-y-auto">
                    {filteredMenuItems.length > 0 ? (
                      <div className="divide-y">
                        {filteredMenuItems.map((item) => (
                          <div key={item.id} className="flex items-center justify-between p-3 hover:bg-muted/50">
                            <div className="flex-1">
                              <p className="text-sm font-medium">{item.name}</p>
                              <p className="text-xs text-muted-foreground">{item.price}</p>
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => addMenuItem(item)}
                              className="h-8"
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Add
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-sm text-muted-foreground py-4">No items found</p>
                    )}
                  </div>
                )}

                {/* Selected Items */}
                {cateringForm.selectedItems.length > 0 && (
                  <div className="mt-3 border rounded-md p-2 md:p-4 bg-muted/30">
                    <p className="text-[10px] md:text-xs font-semibold mb-2 md:mb-3">Selected Items ({cateringForm.selectedItems.length})</p>
                    <div className="space-y-1.5 md:space-y-2">
                      {cateringForm.selectedItems.map((item, index) => (
                        <div key={item.itemId} className="flex items-start md:items-center gap-1 md:gap-2 bg-background p-1.5 md:p-3 rounded text-[10px] md:text-sm">
                          <span className="text-[9px] md:text-xs text-muted-foreground min-w-[12px] md:min-w-[16px]">{index + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate leading-tight">{item.itemName}</div>
                            <div className="flex items-center gap-1 mt-0.5 md:hidden">
                              <span className="text-[9px] text-muted-foreground">{item.price}</span>
                              <span className="text-[9px] text-muted-foreground">×</span>
                              <Input
                                type="number"
                                min="1"
                                value={itemQuantities[item.itemId] || 1}
                                onChange={(e) => updateItemQuantity(item.itemId, parseInt(e.target.value) || 1)}
                                className="w-10 h-5 text-[10px] p-0.5 text-center"
                                placeholder="Qty"
                              />
                            </div>
                          </div>
                          <div className="hidden md:flex items-center gap-2">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">{item.price}</span>
                            <span className="text-xs text-muted-foreground">×</span>
                            <Input
                              type="number"
                              min="1"
                              value={itemQuantities[item.itemId] || 1}
                              onChange={(e) => updateItemQuantity(item.itemId, parseInt(e.target.value) || 1)}
                              className="w-16 h-8 text-xs"
                              placeholder="Qty"
                            />
                          </div>
                          <span className="text-[10px] md:text-xs font-semibold min-w-[40px] md:min-w-[60px] text-right">
                            ${calculateItemTotal(item.price, item.quantity).toFixed(2)}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeMenuItem(item.itemId)}
                            className="h-5 w-5 md:h-8 md:w-8 p-0"
                          >
                            <X className="h-3 w-3 md:h-4 md:w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 md:mt-4 pt-2 md:pt-3 border-t space-y-1.5 md:space-y-2">
                      <div className="flex justify-between items-center text-xs md:text-sm">
                        <span>Subtotal:</span>
                        <span>${calculateSubtotal().toFixed(2)}</span>
                      </div>
                      
                      <div className="flex justify-between items-center text-xs md:text-sm text-green-600">
                        <span>Discount ({cateringSettings.discount_percentage}%):</span>
                        <span>-${calculateDiscount(calculateSubtotal()).toFixed(2)}</span>
                      </div>
                      
                      <div className="flex justify-between items-center text-xs md:text-sm">
                        <span>Delivery Fee ({cateringSettings.delivery_percentage}%):</span>
                        <span>${calculateDeliveryFee(calculateSubtotal()).toFixed(2)}</span>
                      </div>
                      
                      <div className="flex justify-between items-center text-xs md:text-sm">
                        <span>Sales Tax ({cateringSettings.sales_tax_percentage}%):</span>
                        <span>${calculateTax(calculateSubtotal() - calculateDiscount(calculateSubtotal())).toFixed(2)}</span>
                      </div>
                      
                      <div className="flex justify-between items-center text-xs md:text-sm">
                        <span>Gratuity ({cateringSettings.gratuity_percentage}%):</span>
                        <span>${calculateGratuity(calculateSubtotal() - calculateDiscount(calculateSubtotal())).toFixed(2)}</span>
                      </div>
                      
                      <div className="pt-1.5 md:pt-2 border-t flex justify-between items-center">
                        <span className="text-xs md:text-sm font-bold">Total Quote:</span>
                        <span className="text-base md:text-lg font-bold text-primary">
                          ${calculateGrandTotal().toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="message" className="text-xs md:text-sm">Additional Notes</Label>
                <Textarea
                  id="message"
                  value={cateringForm.message}
                  onChange={handleCateringChange}
                  rows={3}
                  className="mt-2 text-sm md:text-base"
                  placeholder="Special requirements, dietary restrictions, etc..."
                />
              </div>

              <Button type="submit" className="w-full text-sm md:text-base" size="lg" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Request Catering Information'}
              </Button>
            </form>
          </Card>
        </div>
      </main>
    </>
  );
};

export default Catering;
